// The RequestResponseProtocol implementation for WebSockets.

import { AssociatedType } from '@fullstory/typeutils';
import {
  AnyRequestResponseMessage,
  RequestResponseProtocol,
  RequestType,
  ResponseOrException,
  ResponseOrExceptionFor,
} from '../requestresponse';
import { WebSocketLike, WebSocketMessageEvent } from '../link/websocket';

// A WebSocket port. (The unique symbol makes this a distinct type, but at
// runtime this is always a number.)
declare const _portTag: unique symbol;
type WebSocketPort = number | typeof _portTag;

// A message that includes the metadata ("headers", basically) needed by the
// request/response WebSocket protocol.
type WebSocketMessage<M> = {
  sourcePort: WebSocketPort;
  destPort: WebSocketPort;
  data: M;
};

// Reads a frame of WebSocket data and returns the parsed and validated
// WebSocketMessage it contains. Throws an exception if the data is not
// formatted correctly.
function readWebSocketFrame<M>(frame: WebSocketMessageEvent): WebSocketMessage<M> {
  if (typeof frame.data !== 'string') {
    throw new Error(`Non-string message types aren't supported`);
  }

  let jsonData: object;
  try {
    jsonData = JSON.parse(frame.data);
  } catch (e) {
    throw new Error(`Couldn't parse WebSocket message: ${frame.data}`);
  }

  if (
    !jsonData ||
    typeof jsonData !== 'object' ||
    !('destPort' in jsonData) ||
    !('sourcePort' in jsonData)
  ) {
    throw new Error(`WebSocket message has unexpected content: ${frame.data}`);
  }

  return jsonData as WebSocketMessage<M>;
}

// The port on which requests are received. All requests go to the same port;
// the other ports are used for responses.
const requestPort = 0;

// Per-port metadata tracked by the WebSocketPortManager.
type PortInfo = {
  // The WebSocket that owns this port.
  socket: WebSocketLike;
  // Called when the port receives a message to resolve the associated promise.
  resolve: (event: WebSocketMessage<unknown>) => void;
};

// A manager for WebSocket request/response ports.
//
// In the WebSocket request/response protocol this file implements, all messages
// have a source and destination port. All requests are sent to a special
// designated port which is always open and doesn't need to be managed.
// Responses, however, are sent to ephemeral, dynamically created ports; these
// are the ports that WebSocketPortManager is responsible for. Response ports
// are opened when a request is sent and closed when the corresponding response
// is received.
//
// WebSocketPortManager is responsible for two resources. It ensures that each
// open port has a unique port number. It also registers a message listener on
// each socket that at least one open port, and deregisters it when all ports on
// the socket are closed. Note that WebSocketPortManager only registers one
// listener per socket, regardless of how many ports are open on it. If we
// registered a listener per port, performance would degrade when there are many
// outstanding requests, because all listeners would run every time a response
// was received.
//
// WebSocketPortManager associates a promise with each port which is resolved
// when a response is received on that port. It deliberately does not reject if
// the underlying socket is closed; in all existing use cases, socket lifetimes
// are tied to the page or some other resource anyway, and rejecting on close
// would end up requiring a try/catch around every use of sendMessage() without
// much benefit. This is something we could revisit in the future, though.
class WebSocketPortManager {
  private _nextPort: WebSocketPort = 1;
  private readonly _openPorts = new Map<WebSocketPort, PortInfo>();
  private readonly _openSockets = new Map<WebSocketLike, Set<WebSocketPort>>();

  // The listener we attach to sockets which have at least one open port.
  private readonly _responseListener = (event: WebSocketMessageEvent): void => {
    const message = readWebSocketFrame<unknown>(event);

    const portInfo = this._openPorts.get(message.destPort);
    if (portInfo === undefined) {
      throw new Error(`Received message for invalid port: ${JSON.stringify(message)}`);
    }

    // A port only stays open long enough to receive one response. Go ahead and
    // close it now.
    this._closePort(portInfo.socket, message.destPort);

    portInfo.resolve(message);
  };

  // Open a response port on the given socket and pass it to action(). Returns a
  // promise which will resolve with the response when it arrives.
  withResponsePort(
    socket: WebSocketLike,
    action: (port: WebSocketPort) => void,
  ): Promise<WebSocketMessage<unknown>> {
    const [port, promise] = this._openPort(socket);

    try {
      action(port);
    } catch (e) {
      this._closePort(socket, port);
      throw e;
    }

    return promise;
  }

  private _openPort(socket: WebSocketLike): [WebSocketPort, Promise<WebSocketMessage<unknown>>] {
    // Find an unused port.
    let port: WebSocketPort;
    do {
      port = (this._nextPort as number)++;
    } while (port === requestPort || this._openPorts.has(port));

    const promise = new Promise<WebSocketMessage<unknown>>(resolve => {
      // Register the new port.
      this._openPorts.set(port, { socket, resolve });

      // Add it to the list of open ports on the socket. If it's the first one,
      // we need to register the message event listener as well.
      const socketPorts = this._openSockets.get(socket);
      if (socketPorts === undefined) {
        this._openSockets.set(socket, new Set<WebSocketPort>([port]));
        socket.addEventListener('message', this._responseListener);
        return;
      }
      socketPorts.add(port);
    });

    return [port, promise];
  }

  private _closePort(socket: WebSocketLike, port: WebSocketPort): void {
    // Deregister the port.
    this._openPorts.delete(port);

    // Remove it from the list of open ports o nthe socket. If it's the last
    // one, we need to unregister the message event listener as well.
    const socketPorts = this._openSockets.get(socket);
    if (socketPorts === undefined) {
      throw new Error(`Lost track of socket for port ${port.toString()}`);
    }
    if (!socketPorts.delete(port)) {
      throw new Error(`Lost track of port ${port.toString()}`);
    }
    if (socketPorts.size === 0) {
      this._openSockets.delete(socket);
      socket.removeEventListener('message', this._responseListener);
    }
  }
}

const portManager = new WebSocketPortManager();

// The request/response protocol implementation for WebSockets.
export class WebSocketRequestResponseProtocol<
  M extends AnyRequestResponseMessage,
> extends RequestResponseProtocol<M> {
  // WebSocket data arrives as a message event.
  _FrameType!: AssociatedType<WebSocketMessageEvent>;
  // Requests are sent to a socket.
  _RequestReceiverType!: AssociatedType<WebSocketLike>;
  // Responses are sent to a (socket, port) pair.
  _ResponseReceiverType!: AssociatedType<[WebSocketLike, WebSocketPort]>;
  // No additional metadata for requests / responses is defined.
  _MetadataType: AssociatedType<undefined>;

  protected readRequest(
    frame: WebSocketMessageEvent,
  ): [RequestType<M>, [WebSocketLike, WebSocketPort]] | undefined {
    const message = readWebSocketFrame<RequestType<M>>(frame);
    if (message.destPort !== requestPort) {
      return undefined; // This is a response, not a request. Ignore it.
    }

    return [message.data, [frame.source, message.sourcePort]];
  }

  protected sendRequest<Request extends RequestType<M>>(
    receiver: WebSocketLike,
    request: Request,
  ): Promise<ResponseOrExceptionFor<M, Request>> {
    return portManager.withResponsePort(receiver, (port: WebSocketPort) => {
      const wrappedMessage: WebSocketMessage<RequestType<M>> = {
        sourcePort: port,
        destPort: requestPort,
        data: request,
      };
      receiver.send(JSON.stringify(wrappedMessage));
    }) as Promise<ResponseOrExceptionFor<M, Request>>;
  }

  protected sendResponse(
    [socket, destPort]: [WebSocketLike, WebSocketPort],
    response: ResponseOrException<M>,
  ): void {
    const wrappedResponse: WebSocketMessage<ResponseOrException<M>> = {
      sourcePort: requestPort,
      destPort,
      data: response,
    };
    socket.send(JSON.stringify(wrappedResponse));
  }
}
